import { useState, useCallback, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router";
import isEqual from "lodash/isEqual";
import moment from "moment";

import {
  ADD_OPEN_MODAL,
  CONFIRM_MODAL,
  SET_SOP_MODAL_STATUS,
  SOP_TABS,
} from "../../../constants";
import useAssociatedFiles from "../../../hooks/useAssociatedFiles";
import useEditingResourceState from "../../../hooks/useEditingResourceState";
import useEvents from "../../../hooks/useEvents";
import useQueryNotFoundNavigation from "../../../hooks/navigation/useQueryNotFoundNavigation";
import useSopFormReducer from "../../../hooks/useSopFormReducer";
import useURLQueryParameter from "../../../hooks/useURLQueryParameter";
import useWidgetTabNavigation from "../../../hooks/useWidgetTabNavigation";

import { getSingleResourcePath } from "../../../helpers/Navigation";
import { onUpdateFile } from "../../../helpers/File";
import {
  toastError,
  toastMessage,
} from "../../../stories/Components/Toast/Toast";
import { useAppState } from "../../../state/appState";
import { useModalState } from "../../../state/modalState";
import {
  usePatchSop,
  useSopById,
  useSopByIdPrefetch,
} from "../../../hooks/useSopById";
import { useSopClone } from "../../../hooks/sop";
import useCurrentUser from "../../../hooks/useCurrentUser";

const useSopDetailsData = () => {
  const history = useHistory();
  const { data: currentUser } = useCurrentUser();

  const { sopId } = useParams();

  const [, reloadEvents] = useEvents({
    startDate: moment()
      .startOf("month")
      .startOf("day")
      .subtract(3, "months")
      .format(),
    endDate: moment().endOf("month").endOf("day").add(3, "months").format(),
    user: currentUser?.reference,
  });

  const { data: sop, isLoading: loading, error } = useSopById(sopId);

  // Redirect to 404 page if resource is not found
  useQueryNotFoundNavigation({ error });

  // If `version` parameter available, `useSopId` will download that specific sop version
  const version = useURLQueryParameter("version");

  // Prefetch the previous SOP version to avoid loading status in history tab
  useSopByIdPrefetch(sopId, version || sop?.version - 1);

  const { cloneSop } = useSopClone();

  const [{ systemConfiguration, sopModalStatus }, dispatch] = useAppState();

  const [showActivity, setShowActivity] = useState(false);

  const disableEditing = !currentUser?.hasPermission?.(
    "administrative",
    "can_write_property"
  );

  const disableUploadMedia = !currentUser?.hasPermission?.("sop", "can_write");

  const handleNavigationBack = () => {
    if (showActivity) {
      setShowActivity(false);
    } else {
      history.goBack();
    }
  };

  /**
   * Clone current SOP and redirect to single SOP page
   * @param {String} id - Given SOP id
   */
  const handleCloneSop = useCallback(
    (id) => {
      cloneSop(id)
        .then((newSop) => {
          // Always set tabIndex to 0 after cloning a sop
          dispatch({
            type: SET_SOP_MODAL_STATUS,
            activeTabIndex: 0,
          });

          history.push(
            `${getSingleResourcePath(newSop.reference)}?tab=${
              SOP_TABS.DETAILS_ID
            }`
          );

          toastMessage(`Successfully cloned SOP ${sop?.name}`);
        })
        .catch(() => {
          toastError(`Error cloning SOP ${sop?.name}`);
        });
    },
    [cloneSop, history, dispatch, sop?.name]
  );

  const [, modalDispatch] = useModalState();
  const [sopState, sopDispatch] = useSopFormReducer();

  const params = useMemo(() => ({ association: `Sop/${sopId}` }), [sopId]);

  const { mutate: patchSop } = usePatchSop();

  const {
    associatedFiles,
    addFiles,
    cloneFile,
    removeFilesAndUpdateApi,
    patchFile,
  } = useAssociatedFiles(params);

  const [catOptions, setCatOptions] = useState([]);

  const [editing, setEditing] = useState(false);
  const [addNewStep, setAddNewStep] = useState(false);
  const [canDeleteSop, setCanDeleteSop] = useState(false);
  const [someSopInUse] = useState(false);

  // handle editing state of resource being edited
  useEditingResourceState({
    editing,
    resource: "Sop",
  });
  // focus state on last new step added
  const [stepFocus, setStepFocus] = useState(false);

  const resetSopState = useCallback(() => {
    sopDispatch({
      type: "reset",
      sop,
    });
  }, [sop, sopDispatch]);

  const reloadSop = useCallback(() => {
    sopDispatch({
      type: "initialize",
      payload: sop,
    });
  }, [sop, sopDispatch]);

  useEffect(() => {
    reloadSop();
  }, [reloadSop]);

  useEffect(() => {
    const categories = systemConfiguration?.system?.sop?.category;
    setCatOptions(() => {
      const catList = categories?.map((cat) => {
        return { label: cat.display, value: cat.id };
      });
      return catList;
    });
  }, [sop?.category, systemConfiguration?.system?.sop?.category]);

  const onEditSubmit = useCallback(() => {
    patchSop({
      id: sopId,
      prevSop: sop,
      updatedSop: {
        ...sopState,
        tags: sopState?.currentTags?.map((tag) => tag?.value) || [],
        steps: sopState?.steps?.filter((step) => step?.description !== ""),
      },
      onAdditionalSuccessCallback: () => {
        // Use setTimeout to simulate a delayed action
        setTimeout(() => {
          // reload events/tasks after 10sec
          reloadEvents();
        }, 10000);
      },
    });
  }, [patchSop, reloadEvents, sop, sopId, sopState]);

  const onFinishEditing = useCallback(async () => {
    if (!isEqual(sop, sopState)) {
      // open warning modal
      modalDispatch({
        type: ADD_OPEN_MODAL,
        ref: { id: `edit-sop-${sop.id}` },
        modalData: {
          item: {
            prompt:
              "Are you sure you want to update this SOP, by updating this you will be updating all scheduled tasks where this SOP is being used? This action can not be undone",
            onConfirm: () => onEditSubmit(),
            confirm: "Yes, update",
            cancel: "Cancel",
            title: "Update SOP",
            modalWidth: "780px",
          },
        },
        modalType: CONFIRM_MODAL,
      });
    }
  }, [modalDispatch, onEditSubmit, sop, sopState]);

  const buttonActions = useMemo(() => {
    const options = [];

    if (
      /*
       * remove sopIsActive when SOP isActive prop is enabled in 3.6
       */
      // sopIsActive &&
      // !currentSop?.isActive &&
      currentUser.hasPermission("sop", "can_delete")
    ) {
      options.push({
        onClick: () => setCanDeleteSop(true),
        title: "Delete Sop",
      });
    }
    if (currentUser.hasPermission("sop", "can_write")) {
      options.push({
        onClick: () => handleCloneSop(sopId),
        title: "Clone Sop",
        disabled: !sop?.category,
      });
    }

    return options;
  }, [currentUser, setCanDeleteSop, handleCloneSop, sopId, sop?.category]);

  const handleAddMedia = useCallback(
    (imageResources) => {
      const updatedFiles = [
        ...sop.files,
        ...imageResources?.map((imageResource) => ({
          ref: imageResource.reference,
          category: imageResource.category,
        })),
      ];

      const primary =
        sop.primaryImage ||
        updatedFiles.find((file) => file.category === "Photos")?.ref;

      const updatedSop = {
        ...sop,
        files: updatedFiles,
        primaryImage: primary,
      };
      patchSop({
        id: sopId,
        prevSop: sop,
        updatedSop,
        onAdditionalSuccessCallback: () => {
          // update associated files state
          addFiles(imageResources);
        },
      });
    },
    [addFiles, patchSop, sop, sopId]
  );

  const handleSetPrimaryMedia = useCallback(
    (imageRef) => {
      const updatedSop = {
        ...sop,
        primaryImage: imageRef,
      };
      // patch resource
      patchSop({
        id: sopId,
        prevSop: sop,
        updatedSop,
      });
    },
    [patchSop, sop, sopId]
  );

  const handleRemoveMedia = useCallback(
    (imageRefs) => {
      const updatedFiles = sop.files.filter(
        (file) => !imageRefs.includes(file.ref)
      );
      const primary = imageRefs.includes(sop?.primaryImage)
        ? updatedFiles.find((file) => file.category === "Photos")?.ref
        : sop.primaryImage;
      const updatedSop = {
        ...sop,
        files: updatedFiles,
        primaryImage: primary,
      };

      patchSop({
        id: sopId,
        prevSop: sop,
        updatedSop,
        onAdditionalSuccessCallback: () => {
          // update associated files state
          removeFilesAndUpdateApi(imageRefs);
        },
      });
    },
    [patchSop, removeFilesAndUpdateApi, sop, sopId]
  );

  const updateSopFiles = useCallback(
    (fileRefs) => {
      const updatedFiles = sop.files.filter(
        (file) => !fileRefs.includes(file.ref)
      );
      const primary = fileRefs.includes(sop?.primaryImage)
        ? updatedFiles.find((file) => file.category === "Photos")?.ref
        : sop.primaryImage;
      const updatedSop = {
        ...sop,
        files: updatedFiles,
        primaryImage: primary,
      };
      // patch resource
      patchSop({
        id: sopId,
        prevSop: sop,
        updatedSop,
      });
    },
    [patchSop, sop, sopId]
  );

  const onAddFilesCallback = useCallback(
    async (filesUploaded) => {
      const updatedFiles = [
        ...sop.files,
        ...filesUploaded.map((file) => ({
          ref: file.reference,
          category: file.category,
        })),
      ];

      const updatedSop = {
        ...sop,
        files: updatedFiles,
        primaryImage:
          sop?.primaryImage ||
          updatedFiles.find((file) => file.category === "Photos")?.ref,
      };

      patchSop({
        id: sopId,
        prevSop: sop,
        updatedSop,
        onAdditionalSuccessCallback: () => {
          // update associated files state
          addFiles(filesUploaded);
        },
      });
    },
    [sop, patchSop, sopId, addFiles]
  );

  const handleFileClone = useCallback(
    (fileId) => {
      cloneFile(fileId).then((clonedFile) => {
        const updatedSop = {
          ...sop,
          files: [
            ...(sop?.files || []),
            { ref: clonedFile.reference, category: clonedFile.category },
          ],
        };

        patchSop({
          id: sopId,
          prevSop: sop,
          updatedSop,
          onAdditionalErrorCallback: () => {
            // remove created files if PATCH fails
            removeFilesAndUpdateApi([`File/${fileId}`]);
          },
        });
      });
    },
    [cloneFile, patchSop, removeFilesAndUpdateApi, sop, sopId]
  );

  const handleUpdateFile = useCallback(
    ({ originalResource, currentTags, name }) => {
      onUpdateFile({ originalResource, currentTags, name, patchFile });
    },
    [patchFile]
  );

  const { activeTab, setActiveTab } = useWidgetTabNavigation();

  const showEditIcon = activeTab === SOP_TABS.DETAILS_ID;

  useEffect(() => {
    if (sopModalStatus.activeTabIndex === SOP_TABS.DETAILS_ID) {
      setActiveTab(SOP_TABS.DETAILS_ID);
      dispatch({
        type: SET_SOP_MODAL_STATUS,
        activeTabIndex: undefined,
      });
    }
  }, [dispatch, sopModalStatus, setActiveTab]);

  const currentlyOnEditableTab = activeTab === SOP_TABS.DETAILS_ID;

  const handleEditClick = useCallback(() => {
    setStepFocus(false);

    if (!editing && currentlyOnEditableTab) {
      setEditing(true);
      return;
    }
    setEditing(false);
  }, [editing, currentlyOnEditableTab]);

  const handleChangeName = useCallback(
    (name) => {
      sopDispatch({
        type: "editName",
        payload: name,
      });
    },
    [sopDispatch]
  );

  return {
    addNewStep,
    associatedFiles,
    catOptions,
    currentUser,
    disableEditing,
    disableUploadMedia,
    editing,
    handleAddMedia,
    handleCloneSop,
    handleFileClone,
    handleNavigationBack,
    handleRemoveMedia,
    handleSetPrimaryMedia,
    handleUpdateFile,
    loading,
    onAddFilesCallback,
    reloadEvents,
    removeFilesAndUpdateApi,
    setShowActivity,
    showActivity,
    sop,
    sopDispatch,
    sopId,
    sopState,
    stepFocus,
    updateSopFiles,
    setAddNewStep,
    setStepFocus,
    handleChangeName,
    buttonActions,
    onFinishEditing,
    activeTab,
    setActiveTab,
    resetSopState,
    showEditIcon,
    handleEditClick,
    someSopInUse,
    canDeleteSop,
    setCanDeleteSop,
  };
};

export default useSopDetailsData;
